import {
  Injectable,
  Inject,
  Optional,
  PLATFORM_ID,
  inject,
} from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpHeaders,
} from "@angular/common/http";
import { catchError, Observable, of, throwError } from "rxjs";
import { Router } from "@angular/router";
import { isPlatformServer } from "@angular/common";
import { REQUEST } from "src/express.tokens";
import { AuthenticationService } from "../services/authentication.service";
import { HomePageService } from "src/app/Customer/services/home-page.service";
import { CartService } from "src/app/Shared/services/cart.service";
import { environment } from "src/environments/environment";
import { JwtHelperService } from "@auth0/angular-jwt";
import { finalize } from "rxjs/operators";
import { InitializationService } from "src/utils/initialization.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private homePageService = inject(HomePageService);
  private cartService = inject(CartService);
  private jwtHelper = inject(JwtHelperService);
  private authService = inject(AuthenticationService);
  private initializationService = inject(InitializationService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private request: any
  ) {}

  private readonly urlToExclude = [
    `${environment.apiUrl}Accounts/Vendor`,
    `${environment.apiUrl}Accounts/VendorEdit/VerifyOtp`,
    `${environment.apiUrl}Vendor/FetchVendorAgreementDocuments`,
  ];

  private get baseUrl(): string {
    return environment.production
      ? this.initializationService.getCurrentUrl()
      : environment.baseUrl;
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    let headers = request.headers.set("Accept", "application/json");
    if (isPlatformServer(this.platformId)) {
      headers = headers.set("X-Client-Identifier", "Angular-SSR-Client");
      if (this.request?.headers) {
        const origin = this.request.headers.origin || this.baseUrl;
        headers = headers.set("Origin", origin);
        if (this.request.headers.cookie) {
          headers = headers.set("Cookie", this.request.headers.cookie);
        }
      }
    }

    // Inject SSR headers if server-side
    if (isPlatformServer(this.platformId) && this.request?.headers) {
      const origin = this.request.headers.origin || this.baseUrl;
      headers = headers.set("Origin", origin);

      // Optionally, forward other headers like cookies if needed
      if (this.request.headers.cookie) {
        headers = headers.set("Cookie", this.request.headers.cookie);
      }
    }

    if (request.url.includes("fastBeetle")) {
      headers = headers.set(
        "Authorization",
        `Bearer ${environment.logisticToken}`
      );
    } else if (
      (request.method === "PUT" || request.method === "GET") &&
      this.isUrlExcluded(request.url)
    ) {
      headers = headers.set(
        "Authorization",
        `Bearer ${this.homePageService.configurations.token}`
      );
    } else {
      headers = headers.set(
        "Authorization",
        `Bearer ${this.homePageService.configurations.token}`
      );
    }

    const authReq = request.clone({ headers });

    return next.handle(authReq).pipe(
      catchError((err) => {
        if (
          typeof err.error === "string" &&
          err.error.startsWith("<!DOCTYPE")
        ) {
          console.warn("⚠️ SSR API returned HTML instead of JSON");
          return of({} as HttpEvent<any>);
        }

        if (err.status === 401) {
          this.handleAuthError();
          return of(err);
        }

        return throwError(() => err);
      }),
      finalize(() => {
        // Optional: log or track request end
      })
    );
  }

  private handleAuthError(): void {
    const user = this.authService.getUser();
    const isTokenExpired = this.jwtHelper.isTokenExpired(user.token as string);
    if (!isTokenExpired) return;
    if (isTokenExpired) {
      localStorage.clear();
      this.authenticationService.removeCurrentUser();
    }

    this.homePageService.FetchForBaseUrl(this.baseUrl).subscribe({
      next: (res: any) => {
        if (res?.data?.[0]?.token) {
          this.initializationService.loadPermissions(res.data[0].token);
          this.homePageService.configurations = res.data[0];
        }
      },
      error: () => {
        this.cartService.setCartItemsCount(0);
        this.authenticationService.removeCurrentUser();
      },
    });
  }

  private navigateAfterAuthError(): void {
    const currentUrl = this.initializationService.getCurrentUrl();
    if (!currentUrl) return;

    const isVendor =
      currentUrl.includes("seller") ||
      currentUrl.includes("wholesaler") ||
      currentUrl === "localhost:4500" ||
      currentUrl === "localhost:4700";

    this.router.navigate([
      isVendor ? "/auth/my-account" : "/home/main-dashboard",
    ]);
  }

  private isUrlExcluded(url: string): boolean {
    return this.urlToExclude.includes(url.split("?")[0]);
  }
}
